import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, HeaderGroup, Table, useReactTable } from '@tanstack/react-table'
import { ReactNode } from 'react';

import OutlineSVG from "../../assets/images/pngs/outline.png";
import Pagination from "./Pagination";
import EmptyTable from "./EmptyTable";
import OpacityLoader from '../loaders/OpacityLoader';

interface Props<TData> {
  columns: ColumnDef<TData, string>[];
  data: TData[]
  size: number
  count: number
  pageIndex: number
  loadingState: boolean
  setPageIndex: (index: number) => void
  footerContent?: ReactNode
  hidePagination?: boolean
}


const DataTable = <TData extends object>({ columns, data, size, count, pageIndex, loadingState, setPageIndex, footerContent, hidePagination = false }: Props<TData>) => {

  const table: Table<any> = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const renderFooter = footerContent ?? (!hidePagination ? (
    <Pagination size={size} count={count} pageIndex={pageIndex} pageSize={data.length < size ? data.length : size}
      setPageIndex={setPageIndex} />
  ) : null)

  return (
    <>
      {loadingState && <OpacityLoader/>}
      <div className="table-responsive table-responsive-md" style={{minHeight: `${data.length > 0 ? "50vh": "inherent"}`}}>
        <table className="table table-striped text-nowrap mt-2">
          <thead>
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<any>) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} className={header.column.getCanSort() ? 'pointer-event' : ''}>
                    {header.isPlaceholder ? null : (
                      <span
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === 'asc' ? 'Sort ascending'
                              : header.column.getNextSortingOrder() === 'desc' ? 'Sort descending' : 'Clear sort' : ""
                        }>
                        {flexRender(header.column.columnDef.header, header.getContext())} {header.column.getCanSort()}
                        {header.column.getCanSort() ?
                          {
                            asc: <i className="bx bx-caret-up font-size-15 align-middle ps-1" />,
                            desc: <i className="bx bx-caret-down font-size-15 align-middle ps-1" />,
                          }[header.column.getIsSorted() as string] ?? <i className="bx bxs-sort-alt font-size-13 align-middle ps-1" /> : null}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(data && data.length > 0) ?
        renderFooter :
        <EmptyTable icon={OutlineSVG} description="No data or resources available" />
      }
    </>
  )
}

export default DataTable
